/**
 * PRD.218 — Centralized money helpers built on Dinero.js.
 *
 * All payment/accounting math should go through these helpers so the
 * codebase has one consistent way to interpret currency values.
 *
 * Storage conventions in PowerCareAI:
 *   - invoice_payments.amount_cents — integer minor units (cents)
 *   - billing_adjustments.amount_cents — integer minor units (cents)
 *   - appointment_services.price_cents — integer minor units (cents)
 *   - invoices.{grand_total, subtotal, discount_total, tax_total} —
 *     numeric(12,2) decimal-major units (e.g. 100.50 = ₱100.50)
 *   - invoice_items.{unit_price, discount} — numeric(12,2) decimal-major
 *   - inventory_items.{selling_price, …} — numeric(12,2) decimal-major
 *
 * Use `phpFromCents` for cents-stored values and `phpFromMajor` for
 * decimal-major values at the boundary; from there, all arithmetic
 * and formatting goes through Dinero so floating-point drift never
 * enters balance math.
 *
 * Rounding policy (clinic-billing best practice):
 *   - Mode: HALF_UP everywhere (a.k.a. "round half away from zero" for
 *     positives). Matches what users intuit ("0.5 always rounds up")
 *     and what Philippine BIR-style tax calculations expect. Set as
 *     Dinero's global default below so multiply / percentage / allocate
 *     / toRoundedUnit / format all use the same mode.
 *   - Major → cents conversion uses an exact string-based path so
 *     values like 0.005 / 2.005 / 0.015 don't silently lose half a
 *     centavo to JS float representation (`0.005 * 100 = 0.4999…94`).
 *   - Internal arithmetic stays in integer cents inside Dinero — no
 *     float drift is possible once a value enters the Money type.
 */

import DineroFactory, { type Dinero } from 'dinero.js';

const CURRENCY: DineroFactory.Currency = 'PHP';
const PRECISION = 2; // cents

// Override Dinero's global (default HALF_EVEN / banker's rounding) so
// every .multiply / .percentage / .allocate / .toRoundedUnit / format
// call uses HALF_UP — the rule users actually expect ("0.5 always
// rounds up") and what Philippine BIR-style tax rules prescribe.
DineroFactory.globalRoundingMode = 'HALF_UP';

export type Money = Dinero;

// ─── Constructors ───

/** Build money from an integer minor-unit (cents) value. Returns ₱0 if null/undefined. */
export function phpFromCents(cents: number | null | undefined): Money {
  const n = cents == null || !Number.isFinite(cents) ? 0 : Math.round(cents);
  return DineroFactory({ amount: n, currency: CURRENCY, precision: PRECISION });
}

/**
 * Exact major→cents conversion that avoids the `num * 100` IEEE-754
 * gotcha. Numbers like 0.005, 2.005, 0.015 produce values like
 * 0.49999999999999994 when multiplied by 100, which `Math.round` then
 * rounds DOWN, silently losing the half-centavo. We instead read the
 * canonical decimal representation (toFixed(2) — HALF_UP for finite
 * positives in V8 / SpiderMonkey / JavaScriptCore) and assemble cents
 * by string slicing, so no float multiplication enters the path.
 */
function exactMajorToCents(num: number): number {
  if (!Number.isFinite(num)) return 0;
  const sign = num < 0 ? -1 : 1;
  const fixed = Math.abs(num).toFixed(2); // e.g. "100.55", "0.01"
  const dot = fixed.indexOf('.');
  if (dot === -1) {
    return sign * parseInt(fixed, 10) * 100;
  }
  const whole = parseInt(fixed.slice(0, dot), 10);
  const fracStr = fixed.slice(dot + 1, dot + 3).padEnd(2, '0');
  const frac = parseInt(fracStr, 10);
  return sign * (whole * 100 + frac);
}

/**
 * Build money from a major-unit value (e.g. 100.50). Accepts the decimal
 * shapes Postgres `numeric` can produce in PowerSync clients (number or
 * string). Uses exact string-based rounding so values like `0.005` or
 * `2.005` don't lose their final half-centavo to float representation.
 */
export function phpFromMajor(major: number | string | null | undefined): Money {
  if (major == null) return phpFromCents(0);
  const num = typeof major === 'string' ? parseFloat(major) : major;
  if (!Number.isFinite(num)) return phpFromCents(0);
  return phpFromCents(exactMajorToCents(num));
}

export function phpZero(): Money {
  return phpFromCents(0);
}

// ─── Arithmetic ───

export function addMoney(a: Money, b: Money): Money {
  return a.add(b);
}

export function subtractMoney(a: Money, b: Money): Money {
  return a.subtract(b);
}

export function sumMoney(values: Money[]): Money {
  return values.reduce((acc, v) => acc.add(v), phpZero());
}

/**
 * Multiply money by a unit-less scalar (e.g. an invoice line quantity).
 * Dinero handles the rounding internally so a price × integer qty never
 * drifts into 0.30000000000000004 territory.
 */
export function multiplyMoney(m: Money, multiplier: number): Money {
  if (!Number.isFinite(multiplier)) return phpZero();
  return m.multiply(multiplier);
}

/**
 * Compute `m × (percent / 100)`. Useful for tax-on-base calculations
 * where `taxRate` is expressed as a number like `12` (meaning 12%).
 * Negative or non-finite percentages return ₱0 rather than throwing,
 * matching the defensive behaviour of the other helpers.
 */
export function percentageOf(m: Money, percent: number): Money {
  if (!Number.isFinite(percent) || percent <= 0) return phpZero();
  return m.percentage(percent);
}

// ─── Predicates ───

export function isZero(m: Money): boolean {
  return m.isZero();
}

export function isPositive(m: Money): boolean {
  return m.isPositive() && !m.isZero();
}

export function isNegative(m: Money): boolean {
  return m.isNegative();
}

export function lessThanOrEqual(a: Money, b: Money): boolean {
  return a.lessThanOrEqual(b);
}

export function greaterThan(a: Money, b: Money): boolean {
  return a.greaterThan(b);
}

// ─── Accessors ───

/** Whole cents as an integer. Use this when persisting to invoice_payments.amount_cents. */
export function toCents(m: Money): number {
  return m.getAmount();
}

/** Major-unit number (e.g. 100.5). Avoid for math — only for legacy display fallbacks. */
export function toMajor(m: Money): number {
  return m.toRoundedUnit(PRECISION);
}

// ─── Formatting ───

/**
 * Format as Philippine peso with thousands separators and 2 fraction digits.
 * Example: ₱1,234.50
 */
export function formatPHP(m: Money): string {
  return `₱${m.toRoundedUnit(PRECISION).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format without fraction digits — useful for compact chips. Example: ₱1,235 */
export function formatPHPCompact(m: Money): string {
  return `₱${Math.round(m.toRoundedUnit(PRECISION)).toLocaleString('en-US')}`;
}

// ─── Parsing user input ───

/**
 * Parse a free-text amount typed into a payment form. Accepts "100",
 * "100.5", "1,234.50", " 1234 ". Returns null when the input isn't a
 * positive numeric value (caller should display a validation error).
 */
export function parseAmountInput(input: string): Money | null {
  const cleaned = input.replace(/,/g, '').trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  if (!Number.isFinite(num) || num < 0) return null;
  return phpFromMajor(num);
}
